﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// A page for the second step in the password reset workflow. Displays an input field where the user can
// enter a validation code in order to reset their password.

// Can be tested in jRåd.
// Click on the "Forgot username or password" link at the login page.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { useEffect } from "react";
import { TitleText } from "~base/Components/Primitives/TitleText";
import Grid from "~base/Components/Layout/Grid";
import { useIntl } from "~base/Internationalization/IntlContext";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import classnames from "classnames";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import TitleButton from "~base/Components/Primitives/TitleButton";
import { reducer } from "./Store";
import { useSearchParams } from "react-router-dom";
import { useSettings } from "~base/Settings/SettingsContext";
import Form from "~base/Components/Primitives/Form";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import { useStyles } from "./Styles";
import { useNavigateService } from "~base/Services/NavigateService";
import TextBlock from "~base/Components/Primitives/TextBlock";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type ResetCodeProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const ResetCodePage: React.FunctionComponent<ResetCodeProps> = (
    props: ResetCodeProps
) => {
    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("ResetCodePage"),
        reducer,
        { code: "", navigating: false, isValid: false }
    );

    const settings = useSettings();
    const [searchParams] = useSearchParams();
    const navigate = useNavigateService();

    const username = searchParams.get("username");
    const serverVirtualPath = settings.valueAsString("serverVirtualPath");

    const detectedTenant = settings.valueAsString("detectedTenantName");

    const tenantPart = detectedTenant ? `/${detectedTenant}` : "";
    const setPasswordBaseUrl = `${serverVirtualPath}Account/SetPassword${tenantPart}?`;

    const setPasswordUrl = `${setPasswordBaseUrl}username=${encodeURIComponent(username)}&token=${encodeURIComponent(state.code)}`;

    useEffect(() => {
        if (state.navigating) {
            navigate.current(setPasswordUrl);
        }
    }, [navigate, setPasswordUrl, state.navigating]);

    const classes = useStyles();

    const jssSpacingStyles = useJssSpacingStyles();

    const formVerticalSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        horizontalCenterPadding
    );

    const intl = useIntl();

    return (
        <ApplicationPage isLoading={state.navigating}>
            <Form
                testIdentifier="ResetCodeForm"
                onSubmit={() => dispatch({ type: "navigateNextPage" })}
                isValid={state.isValid}
            >
                <Rubrik
                    textCode="VisiFinance.UI.Reset.Title"
                    rubrikSizeType="Medium"
                />
                <TextBlock
                    styleType="Paragraph"
                    textCode="VisiFinance.UI.Reset.SentMessage"
                />
                <TextBlock
                    styleType="Paragraph"
                    textCode="VisiFinance.UI.Reset.ResetPassword.Instructions"
                    bold={true}
                />

                <Grid className={classes.grid} templateColumns={null}>
                    <TitleText
                        testIdentifier="ResetCodeInput"
                        title=" "
                        value={state.code}
                        autoFocus={true}
                        maxLength={6}
                        isValid={(x) => x.length == 6}
                        setValue={(x) =>
                            dispatch({ type: "updateCode", code: x })
                        }
                    />

                    <TitleButton
                        testIdentifier="ResetCodeSubmit"
                        title=" "
                        enabled={state.isValid}
                        submit={true}
                        styleType={"DefaultPadded"}
                        buttonClassName={classnames(formVerticalSpacingClasses)}
                    >
                        {intl.formatText(
                            "VisiFinance.UI.Reset.ResetPassword.Submit"
                        )}
                    </TitleButton>
                </Grid>
                <TextBlock
                    styleType="Paragraph"
                    textCode="VisiFinance.UI.Reset.ResetPassword.TextCodeValidTime"
                />
            </Form>
        </ApplicationPage>
    );
};
